import { useCallback, useMemo, useRef, useState } from "react";
import { Search, ChevronRight, ChevronDown, Save, Wand2, AlertTriangle, X, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import MatchCard from "./MatchCard";

function RoundHeader({ round, t, expanded, onToggle, matchCount }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            data-testid={`group-toggle-${round}`}
            aria-expanded={expanded}
            className="mb-3 flex items-center gap-1.5 font-heading text-xs uppercase tracking-[0.3em] font-bold text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
            {expanded
                ? <ChevronDown className="h-3.5 w-3.5" />
                : <ChevronRight className="h-3.5 w-3.5" />}
            <span>{round}</span>
            <span className="text-[var(--text-muted)]/70 tracking-widest">({matchCount})</span>
        </button>
    );
}

/** Pick the rounds that should be auto-opened on first load.
 *  Rule: open the rounds containing the next 2 upcoming non-finished
 *  matches, capped at 2 distinct rounds. */
function nextUpcomingRounds(grouped, max = 2, maxRounds = 2) {
    const now = Date.now();
    const candidates = [];
    grouped.forEach(([round, list]) => {
        list.forEach((m) => {
            const ts = m.kickoff ? new Date(m.kickoff).getTime() : null;
            if (m.status === "finished" || (ts != null && ts <= now)) return;
            if (ts == null) return;
            candidates.push({ round, ts });
        });
    });
    candidates.sort((a, b) => a.ts - b.ts);
    const picked = new Set();
    for (const c of candidates.slice(0, max)) {
        picked.add(c.round);
        if (picked.size >= maxRounds) break;
    }
    return picked;
}

function AutoPredictModal({ open, onClose, onDone, poolId }) {
    const [limit, setLimit] = useState(5);
    const [overwrite, setOverwrite] = useState(false);
    const [randomness, setRandomness] = useState(35); // 0..100
    const [busy, setBusy] = useState(false);
    if (!open) return null;
    const submit = async () => {
        setBusy(true);
        try {
            const body = {
                limit: limit === "all" ? null : Number(limit),
                overwrite,
                randomness: randomness / 100,
            };
            const { data } = await api.post(`/pools/${poolId}/predictions/auto-fill`, body);
            const total = (data.created || 0) + (data.updated || 0);
            toast.success(`Auto-filled ${total} (${data.created} new · ${data.updated} updated · ${data.skipped_unresolved} unresolved skipped)`);
            onDone?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    // Pre-rendered hint per slider position.
    const randomnessHint =
        randomness === 0 ? "Pure ranking — no randomization (deterministic)" :
        randomness < 30  ? "Mostly rank-driven, light variation" :
        randomness < 70  ? "Rank-aware with meaningful jitter" :
        randomness < 100 ? "Mostly random, ranking barely matters" :
                           "Fully random — ranking ignored";
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="auto-predict-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight flex items-center gap-2">
                        <Wand2 className="h-5 w-5 text-amber-400" /> Auto-predict
                    </h3>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X className="h-4 w-4" /></button>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-3">
                    Fill predictions for upcoming matches in chronological order using current FIFA rankings as a baseline.
                </p>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1.5">How many matches?</label>
                <div className="flex gap-1 mb-3">
                    {[5, 10, "all"].map((opt) => (
                        <button key={opt} type="button" onClick={() => setLimit(opt)} data-testid={`auto-predict-${opt}`}
                            className={`flex-1 px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest ${
                                limit === opt ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-secondary)]"
                            }`}>{opt === "all" ? "All upcoming" : `Next ${opt}`}</button>
                    ))}
                </div>
                {/* Randomness slider — 0 → pure rank, 100 → fully random */}
                <div className="mb-3">
                    <div className="flex items-baseline justify-between mb-1.5">
                        <label htmlFor="auto-predict-randomness" className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                            Randomness
                        </label>
                        <span data-testid="auto-predict-randomness-value" className="font-mono text-xs font-extrabold text-amber-400 tabular-nums">{randomness}%</span>
                    </div>
                    <input
                        id="auto-predict-randomness"
                        data-testid="auto-predict-randomness"
                        type="range"
                        min="0"
                        max="100"
                        step="5"
                        value={randomness}
                        onChange={(e) => setRandomness(Number(e.target.value))}
                        className="w-full accent-amber-500"
                    />
                    <div className="flex justify-between text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)] mt-1">
                        <span>Pure rank</span>
                        <span>Fully random</span>
                    </div>
                    <p className="text-[11px] text-[var(--text-secondary)] mt-1.5">{randomnessHint}</p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer mb-2">
                    <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="auto-predict-overwrite" />
                    Overwrite existing predictions
                </label>
                {overwrite && (
                    <div className="flex items-start gap-2 text-xs text-amber-400 mb-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>This will replace any picks you&apos;ve already saved for the selected matches.</span>
                    </div>
                )}
                <div className="mt-4 flex items-center justify-end gap-2">
                    <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                        Cancel
                    </button>
                    <button onClick={submit} disabled={busy} data-testid="auto-predict-confirm"
                        className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
                        {busy ? "…" : "Auto-fill"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function FixturesView({ grouped, teams, predictions, onSave, filter, setFilter, search, setSearch, predictedBracket, poolId, t, onAutoFilled }) {
    const [sortMode, setSortMode] = useState("kickoff"); // "kickoff" | "groups"
    // "upcoming-first" (default) sorts LIVE + future matches chronologically at
    // the top and pushes past matches to the bottom. "asc" is oldest-first,
    // "desc" is newest-first. Only meaningful in kickoff sort mode.
    const [sortDir, setSortDir] = useState("upcoming-first");
    const [showAutoPredict, setShowAutoPredict] = useState(false);
    const [showDeleteAll, setShowDeleteAll] = useState(false);
    const [deletingAll, setDeletingAll] = useState(false);
    const [deleteScope, setDeleteScope] = useState("all"); // "all" | "group" | "knockout"

    const deleteAllPredictions = useCallback(async () => {
        setDeletingAll(true);
        try {
            const { data } = await api.delete(`/pools/${poolId}/predictions`, { params: { scope: deleteScope } });
            const label = deleteScope === "knockout" ? " (knockout)" : deleteScope === "group" ? " (group)" : "";
            toast.success(`Deleted ${data.deleted} prediction${data.deleted === 1 ? "" : "s"}${label}`);
            setShowDeleteAll(false);
            setDeleteScope("all");
            onAutoFilled?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setDeletingAll(false); }
    }, [poolId, deleteScope, onAutoFilled]);

    // ── Save-all coordination ───────────────────────────────────────────
    // MatchCard reports `dirty` state up via registerDirty(matchId, payload|null).
    // We keep the pending edits in a ref to avoid re-rendering every keystroke;
    // the visible count is mirrored to a small state for the button label.
    const pendingRef = useRef({});
    const [pendingCount, setPendingCount] = useState(0);
    // Prompt state: when set, holds the pending save info from a single-card
    // save attempt while there are also other dirty cards. The modal lets
    // the user choose to save all dirty cards in one go or just this one.
    const [savePrompt, setSavePrompt] = useState(null); // null | { mid, h, a, pen }
    const registerDirty = useCallback((matchId, payload) => {
        if (payload == null) {
            if (pendingRef.current[matchId] != null) {
                delete pendingRef.current[matchId];
                setPendingCount(Object.keys(pendingRef.current).length);
            }
        } else {
            pendingRef.current[matchId] = payload;
            setPendingCount(Object.keys(pendingRef.current).length);
        }
    }, []);
    const saveAll = useCallback(async () => {
        const entries = Object.entries(pendingRef.current);
        for (const [mid, payload] of entries) {
            try { await onSave(mid, payload.h, payload.a, payload.pen ?? null); } catch { /* toast handled per-card */ }
        }
        pendingRef.current = {};
        setPendingCount(0);
    }, [onSave]);

    // Intercept single-card saves: if there are OTHER dirty cards in flight,
    // pop a confirm dialog asking whether to save all or just this one.
    // Returns a Promise that resolves with `true` when the save was committed
    // (so the card can clear its dirty state) and `false` when the user cancels.
    const promptDeferredRef = useRef(null);
    const handleCardSave = useCallback(async (mid, h, a, pen) => {
        const otherDirty = Object.keys(pendingRef.current).filter((k) => k !== mid).length;
        if (otherDirty === 0) {
            await onSave(mid, h, a, pen);
            return true;
        }
        // Bounce through the modal. Return a deferred so MatchCard can await it.
        return new Promise((resolve) => {
            promptDeferredRef.current = resolve;
            setSavePrompt({ mid, h, a, pen });
        });
    }, [onSave]);

    const confirmSaveJustThis = useCallback(async () => {
        if (!savePrompt) return;
        const { mid, h, a, pen } = savePrompt;
        setSavePrompt(null);
        await onSave(mid, h, a, pen);
        promptDeferredRef.current?.(true);
        promptDeferredRef.current = null;
    }, [savePrompt, onSave]);

    const confirmSaveAllFromPrompt = useCallback(async () => {
        if (!savePrompt) return;
        // Ensure the pressed card's CURRENT (possibly newer) values win.
        pendingRef.current[savePrompt.mid] = { h: savePrompt.h, a: savePrompt.a, pen: savePrompt.pen };
        setSavePrompt(null);
        await saveAll();
        promptDeferredRef.current?.(true);
        promptDeferredRef.current = null;
    }, [savePrompt, saveAll]);

    const cancelSavePrompt = useCallback(() => {
        setSavePrompt(null);
        promptDeferredRef.current?.(false);
        promptDeferredRef.current = null;
    }, []);

    // ── Auto-expand seed (groups mode) ─────────────────────────────────
    // `expanded` is derived: defaults come from `nextUpcomingRounds(grouped)`,
    // overlaid with any per-round user overrides. This avoids set-state-in-effect.
    const defaultExpanded = useMemo(() => {
        const rounds = nextUpcomingRounds(grouped);
        const map = {};
        rounds.forEach((r) => { map[r] = true; });
        return map;
    }, [grouped]);
    const [overrides, setOverrides] = useState({});
    const expanded = useMemo(
        () => ({ ...defaultExpanded, ...overrides }),
        [defaultExpanded, overrides]
    );

    const toggle = (round) =>
        setOverrides((prev) => ({ ...prev, [round]: !(round in prev ? prev[round] : !!defaultExpanded[round]) }));
    const expandAll = () => {
        const all = {};
        grouped.forEach(([round]) => { all[round] = true; });
        setOverrides(all);
    };
    const collapseAll = () => {
        const all = {};
        grouped.forEach(([round]) => { all[round] = false; });
        setOverrides(all);
    };
    const expandNext = () => setOverrides({}); // revert to defaults

    // ── Flat (kick-off) view ───────────────────────────────────────────
    const flatMatches = useMemo(() => {
        const all = grouped.flatMap(([, list]) => list);
        const asc = (x, y) => (x.kickoff || "").localeCompare(y.kickoff || "");
        if (sortDir === "asc") {
            all.sort(asc);
        } else if (sortDir === "desc") {
            all.sort((x, y) => -asc(x, y));
        } else {
            // upcoming-first (default): LIVE first, then future ASC, then past DESC.
            const now = Date.now();
            const bucket = (m) => {
                if (m.status === "live") return 0;
                const ko = m.kickoff ? new Date(m.kickoff).getTime() : Infinity;
                return ko >= now ? 1 : 2;
            };
            all.sort((x, y) => {
                const bx = bucket(x), by = bucket(y);
                if (bx !== by) return bx - by;
                // Within past bucket, most recent first (DESC); elsewhere ASC.
                return bx === 2 ? -asc(x, y) : asc(x, y);
            });
        }
        return all;
    }, [grouped, sortDir]);

    return (
        <div data-testid="fixtures-view">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("fix.search")}
                        data-testid="fixtures-search"
                        className="w-full bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-primary)] pl-9 pr-3 py-2.5 rounded-md focus:outline-none focus:border-[#CCFF00]" />
                </div>
                <div className="flex gap-1 p-1 rounded-md border border-[var(--border-base)] bg-[var(--bg-card)]">
                    {["all", "upcoming", "locked"].map((f) => (
                        <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded ${
                                filter === f ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            }`}>{t(`fix.filter.${f}`)}</button>
                    ))}
                </div>
                <div className="flex gap-1 p-1 rounded-md border border-[var(--border-base)] bg-[var(--bg-card)]" title={t("fix.sort_label")}>
                    {["kickoff", "groups"].map((m) => (
                        <button key={m} onClick={() => setSortMode(m)} data-testid={`fixtures-sort-${m}`}
                            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded ${
                                sortMode === m ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            }`}>{t(`fix.sort_${m}`)}</button>
                    ))}
                </div>
            </div>

            {sortMode === "groups" && grouped.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-4 text-[10px] uppercase tracking-widest font-bold">
                    <button type="button" onClick={expandNext} data-testid="fixtures-expand-next"
                        className="px-2.5 py-1 rounded border border-amber-500/50 text-amber-400 hover:border-amber-500 hover:text-amber-300 transition-colors">
                        {t("fix.expand_next")}
                    </button>
                    <button type="button" onClick={expandAll} data-testid="fixtures-expand-all"
                        className="px-2.5 py-1 rounded border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent-border)] transition-colors">
                        {t("fix.expand_all")}
                    </button>
                    <button type="button" onClick={collapseAll} data-testid="fixtures-collapse-all"
                        className="px-2.5 py-1 rounded border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent-border)] transition-colors">
                        {t("fix.collapse_all")}
                    </button>
                </div>
            )}

            {/* Sticky save-all bar — only renders when ≥2 cards are dirty */}
            {pendingCount >= 2 && (
                <div className="sticky top-2 z-10 mb-3 flex items-center justify-end" data-testid="fixtures-save-all-wrap">
                    <button type="button" onClick={saveAll} data-testid="fixtures-save-all"
                        className="inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-widest text-xs px-3 py-2 rounded shadow-lg hover:bg-[var(--accent-hex-hover)]">
                        <Save className="h-3.5 w-3.5" />
                        {t("fix.save_all", { n: pendingCount })}
                    </button>
                </div>
            )}

            {grouped.length === 0 && (
                <div className="text-center py-12 text-[var(--text-secondary)]">{t("fix.empty")}</div>
            )}

            {sortMode === "kickoff" ? (
                <>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        <button type="button" onClick={() => setShowAutoPredict(true)} data-testid="fixtures-auto-predict"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-amber-500/50 text-amber-400 hover:border-amber-500 hover:text-amber-300 text-[10px] uppercase tracking-widest font-bold transition-colors">
                            <Wand2 className="h-3 w-3" /> Auto-predict upcoming
                        </button>
                        <button type="button" onClick={() => setShowDeleteAll(true)} data-testid="fixtures-delete-all"
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[var(--danger)]/40 text-[var(--danger)] hover:border-[var(--danger)] hover:bg-[var(--danger)]/5 text-[10px] uppercase tracking-widest font-bold transition-colors">
                            <Trash2 className="h-3 w-3" /> Delete all upcoming
                        </button>
                        {/* Sort direction toggle — cycles upcoming-first → asc → desc → back. */}
                        <button
                            type="button"
                            onClick={() => setSortDir((d) => d === "upcoming-first" ? "asc" : d === "asc" ? "desc" : "upcoming-first")}
                            data-testid="fixtures-sort-dir"
                            title={sortDir === "upcoming-first" ? "Upcoming first (default)" : sortDir === "asc" ? "Oldest first" : "Newest first"}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-[var(--border-base)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--accent-border)] text-[10px] uppercase tracking-widest font-bold transition-colors"
                        >
                            {sortDir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}
                            {sortDir === "upcoming-first" ? "Upcoming" : sortDir === "asc" ? "Oldest" : "Newest"}
                        </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3" data-testid="fixtures-flat-list">
                        {flatMatches.map((m) => (
                            <MatchCard
                                key={m.id}
                                match={m}
                                teams={teams}
                                prediction={predictions[m.id]}
                                onSave={handleCardSave}
                                onDirtyChange={registerDirty}
                                predictedSlot={(predictedBracket || {})[m.id]}
                                poolId={poolId}
                            />
                        ))}
                    </div>
                </>
            ) : (
                grouped.map(([round, list]) => {
                    const isOpen = !!expanded[round];
                    return (
                        <div key={round} className="mb-4">
                            <RoundHeader round={round} t={t} expanded={isOpen} onToggle={() => toggle(round)} matchCount={list.length} />
                            {isOpen && (
                                <div className="grid sm:grid-cols-2 gap-3" data-testid={`group-body-${round}`}>
                                    {list.map((m) => (
                                        <MatchCard
                                            key={m.id}
                                            match={m}
                                            teams={teams}
                                            prediction={predictions[m.id]}
                                            onSave={handleCardSave}
                                            onDirtyChange={registerDirty}
                                            predictedSlot={(predictedBracket || {})[m.id]}
                                            poolId={poolId}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            <AutoPredictModal
                open={showAutoPredict}
                onClose={() => setShowAutoPredict(false)}
                onDone={onAutoFilled}
                poolId={poolId}
            />

            {showDeleteAll && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="delete-all-modal">
                    <div className="bg-[var(--bg-card)] border border-[var(--danger)]/50 rounded-lg max-w-md w-full p-6">
                        <div className="flex items-center gap-2 text-[var(--danger)] mb-3">
                            <AlertTriangle className="h-5 w-5" />
                            <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">Delete ALL upcoming predictions</h3>
                        </div>
                        <p className="text-sm text-[var(--text-secondary)] mb-3">
                            Choose what to wipe. Predictions on matches that have already started are always preserved.
                        </p>
                        <fieldset className="space-y-2 mb-4" data-testid="delete-scope-picker">
                            {[
                                { value: "all", label: "All upcoming", hint: "Group + knockout (default)" },
                                { value: "knockout", label: "Knockout only", hint: "Round of 32 / 16 / QF / SF / 3rd / Final" },
                                { value: "group", label: "Group stage only", hint: "Match-day 1, 2, 3" },
                            ].map((opt) => (
                                <label key={opt.value} className="flex items-start gap-2.5 p-2 rounded border border-[var(--border-base)] hover:border-[var(--danger)]/40 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="delete-scope"
                                        value={opt.value}
                                        checked={deleteScope === opt.value}
                                        onChange={() => setDeleteScope(opt.value)}
                                        data-testid={`delete-scope-${opt.value}`}
                                        className="mt-0.5 accent-[var(--danger)]"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-[var(--text-primary)]">{opt.label}</p>
                                        <p className="text-[11px] text-[var(--text-muted)]">{opt.hint}</p>
                                    </div>
                                </label>
                            ))}
                        </fieldset>
                        <p className="text-xs text-[var(--danger)] mb-4 font-bold">This cannot be undone.</p>
                        <div className="flex items-center justify-end gap-2">
                            <button onClick={() => setShowDeleteAll(false)} disabled={deletingAll}
                                className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]">Cancel</button>
                            <button onClick={deleteAllPredictions} disabled={deletingAll}
                                data-testid="delete-all-confirm"
                                className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--danger)] text-white hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed">
                                {deletingAll ? "…" : "Yes, delete all"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Save-all confirmation prompt — pops when the user clicks Save
                on a single card while OTHER cards are also dirty. Lets them
                pick between batching everything or just persisting this one. */}
            {savePrompt && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="save-all-prompt">
                    <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                        <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight mb-3 text-[var(--text-primary)]">
                            {t("fix.save_all.prompt.title")}
                        </h3>
                        <p className="text-sm text-[var(--text-secondary)] mb-5">
                            {t("fix.save_all.prompt.body", { n: pendingCount })}
                        </p>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                            <button
                                onClick={cancelSavePrompt}
                                data-testid="save-all-prompt-cancel"
                                className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                onClick={confirmSaveJustThis}
                                data-testid="save-all-prompt-just-this"
                                className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest border border-[var(--border-base)] text-[var(--text-primary)] hover:border-[var(--accent-border)]"
                            >
                                {t("fix.save_all.prompt.just_this")}
                            </button>
                            <button
                                onClick={confirmSaveAllFromPrompt}
                                data-testid="save-all-prompt-all"
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-[var(--accent-hex)] text-[var(--accent-text)] hover:bg-[var(--accent-hex-hover)]"
                            >
                                <Save className="h-3.5 w-3.5" /> {t("fix.save_all.prompt.all", { n: pendingCount })}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
